import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator'
import { useSearchParams } from 'react-router-dom'

import styles from './BillingFormModal.module.scss'

import CustomModal from 'components/elements/modal/CustomModal'
import InputGroup from 'components/elements/forms/InputGroup'
import CustomInput from 'components/elements/inputs/CustomInput'
import CustomButton from 'components/elements/buttons/CustomButton'
import { ISelectOption } from 'components/elements/inputs/CustomSelect/CustomSelect'
import Flag from 'components/elements/other/Flag'
import CustomSelect from 'components/elements/inputs/CustomSelect'
import capterra from 'assets/images/reviews/capterra.png'
import g2 from 'assets/images/reviews/g2.png'
import { CircleWavyCheckR } from '../../../icons'

import { minimalPaygTopUp, maximumPaygTopUp } from 'utils/constants'
import validUsZips from 'mocks/valid-us-zips.json'
import countriesList from 'mocks/countries-list.json'
import {
  selectBillingModalVisibility,
  selectCouponValidationProcessing,
  selectAppliedCouponInfo,
  selectIsCouponInvalid,
  selectSubscriptionProcessing,
} from 'store/pricing/selectors'
import { PricingActionsTypes } from 'store/pricing/actions'
import { IBillingInfoData, ISubscriptionPlan } from 'models/pricing'
import {
  selectUserFirstName,
  selectUserLastName,
  selectUserCustomerView,
} from 'store/user/selectors'
import { getNormalizedPrice, getPriceWithoutCents } from 'utils/functions'

interface IBillingFormModal {
  onModalClose: () => void
  selectedPlan: ISubscriptionPlan
  activePriceType: string
  onSubmitBillingForm: (data: IBillingInfoData) => void
}

const PlansInfo = {
  Starter: 'Ideal for start-ups',
  Advanced: 'Ideal for growing businesses',
  Professional: 'Access to advanced tools',
  Business: 'Access to enhanced operations',
  payg: 'Annual credits availability - Advanced targeting',
}

const BillingFormModal: React.FC<IBillingFormModal> = (props) => {
  const { onSubmitBillingForm, selectedPlan, activePriceType, onModalClose } =
    props

  const dispatch = useDispatch()

  const availableLocations = countriesList

  const savedFirstName = useSelector(selectUserFirstName)
  const savedLastName = useSelector(selectUserLastName)
  const isBillingModalShown = useSelector(selectBillingModalVisibility)
  const couponValidationProcessing = useSelector(
    selectCouponValidationProcessing
  )
  const appliedCouponInfo = useSelector(selectAppliedCouponInfo)
  const isCouponInvalid = useSelector(selectIsCouponInvalid)
  const subscriptionProcessing = useSelector(selectSubscriptionProcessing)
  const customerInfo = useSelector(selectUserCustomerView)
  const [amount, setAmount] = useState('')
  const [firstName, setFirstName] = useState(savedFirstName || '')
  const [lastName, setLastName] = useState(savedLastName || '')
  const [selectedCountry, setCountry] = useState<ISelectOption | null>(null)
  const [zipCode, setZipCode] = useState('')
  const [isAccordionActive, toggleAccordion] = useState(false)
  const [couponString, setCouponString] = useState('')
  const [isAmountLessThanMin, toggleAmountLessThanMin] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (isBillingModalShown && searchParams.has('coupon')) {
      const coupon = searchParams.get('coupon')

      if (!!coupon) {
        toggleAccordion(true)
        setCouponString(coupon)

        dispatch({
          type: PricingActionsTypes.PRICING_VALIDATE_COUPON,
          coupon: coupon,
        })

        searchParams.delete('coupon')

        const newParams: { [key: string]: string } = {}

        searchParams.forEach((value: string, key: string) => {
          newParams[key] = value
        })

        setSearchParams(newParams)
      }
    }
  }, [dispatch, isBillingModalShown, searchParams, setSearchParams])

  useEffect(() => {
    if (!!customerInfo) {
      if (!!customerInfo.zip_code) {
        setZipCode(customerInfo.zip_code)
      }

      if (
        !!customerInfo.country &&
        // @ts-ignore
        availableLocations[customerInfo.country.toLowerCase()]
      ) {
        const selectedCountry =
          // @ts-ignore
          availableLocations[customerInfo.country.toLowerCase()]

        setCountry({
          label: selectedCountry.name,
          value: customerInfo.country.toLowerCase(),
          icon: (
            <Flag
              countryCode={customerInfo.country.toLowerCase()}
              className={styles.optionFlag}
            />
          ),
        })
      }
    }
  }, [availableLocations, customerInfo])

  const handleModalClose = () => {
    onModalClose()
  }

  const getCountriesOptions = useCallback(() => {
    let options: ISelectOption[] = []

    if (availableLocations) {
      Object.values(availableLocations).forEach((location: any) => {
        options.push({
          label: location.name,
          value: location.code,
        })
      })
    }

    options.sort(function (a, b) {
      if (a.label < b.label) {
        return -1
      }
      if (a.label > b.label) {
        return 1
      }
      return 0
    })

    return options
  }, [availableLocations])

  const countriesOptions = useMemo(
    () => getCountriesOptions(),
    [getCountriesOptions]
  )

  const isAmountErrored = +amount > maximumPaygTopUp || isAmountLessThanMin

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    toggleAmountLessThanMin(false)
    setAmount(e.target.value)
  }

  const handleAmountBlur = () => {
    if (+amount < minimalPaygTopUp) {
      toggleAmountLessThanMin(true)
    }
  }

  const handleCountryChange = (option: any) => {
    setCountry(option)
  }

  const isZipCodeValid = () => {
    if (!!selectedCountry && !!zipCode) {
      if (selectedCountry.value.toUpperCase() === 'US') {
        return validUsZips.indexOf(zipCode) >= 0
      } else if (
        postcodeValidatorExistsForCountry(selectedCountry.value.toUpperCase())
      ) {
        return postcodeValidator(zipCode, selectedCountry.value.toUpperCase())
      } else {
        return true
      }
    } else {
      return true
    }
  }

  const handleCouponChange = (e: any) => {
    setCouponString(e.target.value)

    if (isCouponInvalid) {
      dispatch({
        type: PricingActionsTypes.PRICING_SET_COUPON_VALIDATION_ERROR,
        isCouponInvalid: false,
      })
    }
  }

  if (!selectedPlan) return null

  const isTrialAvailableForPlan =
    !!selectedPlan?.trial_price &&
    selectedPlan?.type &&
    selectedPlan?.type !== 'payg'

  const getPlanPrice = () => {
    if (
      activePriceType &&
      activePriceType !== 'scraping' &&
      activePriceType !== 'unblocker'
    ) {
      return !!selectedPlan?.prices?.monthly?.proxy
        ? getNormalizedPrice(
            selectedPlan?.prices?.monthly?.proxy[activePriceType]
          )
        : 'n/a'
    } else if (activePriceType && activePriceType === 'scraping') {
      return getNormalizedPrice(
        selectedPlan?.prices?.monthly?.scrapper.ecommerce
      )
    } else if (activePriceType && activePriceType === 'unblocker') {
      return getNormalizedPrice(
        selectedPlan?.prices?.monthly?.scrapper.unblocker
      )
    } else {
      return 0
    }
  }

  const planPrice = getPlanPrice()

  // @ts-ignore
  const planMonthlyPrice = getPriceWithoutCents(selectedPlan?.amount?.monthly)

  const isMeasuredInRequests =
    activePriceType === 'scraping' || activePriceType === 'unblocker'

  const getPlanTraffic = () => {
    const traffic = Math.floor(+planMonthlyPrice / +planPrice)

    if (activePriceType) {
      return traffic
    } else {
      return ''
    }
  }

  const planTraffic = getPlanTraffic()

  const getPlanInfoString = () => {
    if (!selectedPlan) return ''

    const label = isMeasuredInRequests ? 'K' : 'GB'

    const pricePerItem = (selectedPlan?.amount?.monthly / +planTraffic).toFixed(2)

    // @ts-ignore
    const planInfo = PlansInfo[selectedPlan?.name] || ''

    if (selectedPlan?.type === 'payg') {
      return PlansInfo.payg
    } else {
      return `${planTraffic} ${label} included - $${pricePerItem}/${label} - ${planInfo}`
    }
  }

  const planInfoString = getPlanInfoString()

  const getPlanPriceString = () => {
    if (!selectedPlan) return ''

    if (selectedPlan?.type === 'payg') {
      return 'No monthly payments'
    } else if (isTrialAvailableForPlan) {
      return `Trial: $${selectedPlan?.trial_price} (then ${selectedPlan?.amount?.monthly}/month)`
    } else {
      return `$${selectedPlan?.amount?.monthly}/month`
    }
  }

  const planPriceString = getPlanPriceString()

  const handleCouponValidate = () => {
    dispatch({
      type: PricingActionsTypes.PRICING_VALIDATE_COUPON,
      coupon: couponString,
    })
  }

  const handleFormSubmit = () => {
    let billingData: IBillingInfoData = {
      plan_id: selectedPlan?.id,
      country: selectedCountry?.value,
      name: `${firstName} ${lastName}`,
      zip_code: zipCode,
    }

    if (!!appliedCouponInfo) {
      billingData.coupon = couponString
    }

    if (selectedPlan?.type === 'payg') {
      billingData.amount = amount
    }

    onSubmitBillingForm(billingData)
  }

  const amountInnerNotice = +amount > 0 ? `Total: $${amount} (+VAT)` : ''

  const iconedCountriesOptions = countriesOptions.map((item) => ({
    ...item,
    icon:
      item.value === 'any' ? null : (
        <Flag countryCode={item.value} className={styles.optionFlag} />
      ),
  }))

  const zipCodeLabel = !isZipCodeValid() ? 'ZIP - incorrect' : 'ZIP'

  const subtitle = isTrialAvailableForPlan
    ? 'Enter your billing address to start your trial'
    : 'Enter your billing address to start your journey'

  const amountError =
    +amount > maximumPaygTopUp
      ? 'Exceeds max limit'
      : isAmountErrored
        ? `${minimalPaygTopUp} USD minimum`
        : ''

  const renderCouponUi = () => {
    if (!appliedCouponInfo) {
      return (
        <div
          className={`
        ${styles.accordion}
        ${isAccordionActive ? styles.active : ''}
      `}
        >
          <button
            className={`
                ${styles.accordionToggle}
                ${isAccordionActive ? styles.active : ''}
              `}
            onClick={() => toggleAccordion(!isAccordionActive)}
          >
            Add coupon
          </button>

          <div
            className={`
                ${styles.accordionContent}
                ${isAccordionActive ? styles.active : ''}
              `}
          >
            <div className={styles.accordionMain}>
              <CustomInput
                inputClassName={styles.couponInput}
                value={couponString}
                onChange={handleCouponChange}
                readOnly={couponValidationProcessing}
                error={isCouponInvalid}
              />

              {couponString.length > 0 && (
                <CustomButton
                  color={'ghost'}
                  className={styles.couponBtn}
                  onClick={handleCouponValidate}
                  loading={couponValidationProcessing}
                  disabled={isCouponInvalid}
                >
                  Apply
                </CustomButton>
              )}
            </div>
          </div>
        </div>
      )
    } else {
      return (
        <div className={styles.appliedCouponInfo}>
          Coupon applied: -{appliedCouponInfo.discount * 100}%
        </div>
      )
    }
  }

  return (
    <CustomModal
      title={'Complete your order'}
      subtitle={subtitle}
      isModalOpen={isBillingModalShown}
      shouldCloseOnOverlayClick
      showCross
      modalWidth={'560px'}
      bluredOverlay
      centeredTitle
      onModalClose={handleModalClose}
    >
      <>
        <div className={styles.planBlock}>
          <div className={styles.planBlockIconWrapper}>
            <CircleWavyCheckR />
          </div>
          <div className={styles.planName}>
            {selectedPlan?.name}
          </div>
          <div className={styles.planInfo}>
            {planInfoString}
          </div>
          <div className={styles.planPrice}>
            {planPriceString}
          </div>
        </div>

        {selectedPlan?.type === 'payg' && (
          <InputGroup
            labelTop={'USD amount to start'}
            semiBoldLabel
            className={styles.paygGroup}
            error={amountError}
            isErrored={isAmountErrored}
          >
            <CustomInput
              innerNotice={amountInnerNotice}
              value={amount}
              type={'number'}
              onChange={handleAmountChange}
              onBlur={handleAmountBlur}
            />
          </InputGroup>
        )}

        <div className={styles.subtitle}>Billing address</div>

        <div className={styles.row}>
          <InputGroup labelTop={'First name'} semiBoldLabel className={styles.group}>
            <CustomInput
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              readOnly={subscriptionProcessing}
            />
          </InputGroup>

          <InputGroup labelTop={'Last name'} semiBoldLabel className={styles.group}>
            <CustomInput
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              readOnly={subscriptionProcessing}
            />
          </InputGroup>
        </div>

        <div className={styles.row}>
          <InputGroup labelTop={'Country'} semiBoldLabel className={styles.group}>
            <CustomSelect
              onChange={handleCountryChange}
              value={selectedCountry}
              name={'billing-info-country'}
              options={iconedCountriesOptions}
              placeholder={'Select country'}
              isSearchable={true}
              disabled={subscriptionProcessing}
            />
          </InputGroup>

          <InputGroup
            labelTop={zipCodeLabel}
            semiBoldLabel
            className={styles.zipGroup}
            isErrored={!isZipCodeValid()}
          >
            <CustomInput
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              readOnly={subscriptionProcessing}
              error={!isZipCodeValid()}
            />
          </InputGroup>
        </div>

        {renderCouponUi()}

        <CustomButton
          wide
          className={styles.submitBtn}
          color={'blue'}
          onClick={handleFormSubmit}
          disabled={
            (selectedPlan?.type === 'payg' && +amount < minimalPaygTopUp) ||
            (selectedPlan?.type === 'payg' && +amount > maximumPaygTopUp) ||
            !firstName ||
            !lastName ||
            !selectedCountry ||
            !zipCode ||
            !isZipCodeValid()
          }
          loading={subscriptionProcessing}
        >
          Next
        </CustomButton>

        <div className={styles.reviews}>
          <img className={styles.reviewItem} src={capterra} alt={'review'} />
          <img className={styles.reviewItem} src={g2} alt={'review'} />
        </div>
      </>
    </CustomModal>
  )
}

export default BillingFormModal
